'use client';

import { useState } from 'react';
import { Menu, MenuItem, Divider, Typography, FormControlLabel, Switch } from '@mui/material';
import Link from 'next/link';
import { RoleSwitcherDialog } from '@/components/dialog/RoleSwitcherDialog';
import { SignInAsDialog } from '@/components/dialog/SignInAsDialog';
import { StopSignInAsDialog } from '@/components/dialog/StopSignInAsDialog';
import { SignOutDialog } from '@/components/dialog/SignOutDialog';
import { useAuthStore } from '@/stores/authStore';
import { PrivilegeEnum } from '@/api/ApiClient';
import { useSnackbar } from '@/hooks/useSnackbar';
import { navigateToShibbolethSignIn } from '@/lib/auth/signInFlow';
import { log } from '@/lib/utils/logger';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { isAutosaveSuppressedByAuth, useAutosaveActive } from '@/lib/autosave/autosaveAuth';
import { useAutosaveStore } from '@/stores/autosaveStore';
import type { LogoutScope } from '@/lib/auth/signOutFlow';

interface ProfileMenuProps {
  anchorEl: null | HTMLElement;
  onClose: () => void;
  onLogin: () => void;
  onSignOutComplete?: () => void;
}

export default function ProfileMenu({ anchorEl, onClose, onLogin, onSignOutComplete }: ProfileMenuProps) {
  const { currentUser, isAuthorized, stopImpersonate, error, logout } = useAuthStore();
  const { showSnackbar } = useSnackbar();
  const [isRoleSwitcherOpen, setRoleSwitcherOpen] = useState(false);
  const [isSignInAsModalOpen, setSignInAsModalOpen] = useState(false);
  const [isStopSignInAsModalOpen, setStopSignInAsModalOpen] = useState(false);
  const [isSignOutDialogOpen, setSignOutDialogOpen] = useState(false);
  const [isSignOutSubmitting, setSignOutSubmitting] = useState(false);
  const handleOpenStopSignInAsModal = () => setStopSignInAsModalOpen(true);
  const handleCloseStopSignInAsModal = () => setStopSignInAsModalOpen(false);
  const handleOpenRoleSwitcher = () => setRoleSwitcherOpen(true);
  const handleCloseRoleSwitcher = () => setRoleSwitcherOpen(false);
  const handleOpenSignInAsModal = () => setSignInAsModalOpen(true);
  const systemConfig = useSystemConfig();
  const setAutosaveEnabled = useAutosaveStore((state) => state.setEnabled);
  const autosaveActive = useAutosaveActive();
  const autosaveSuppressed = isAutosaveSuppressedByAuth(currentUser);
  // Return null if config is not initialized
  if (!systemConfig) {
    return null;
  }

  const handleShibbolethSignin = () => {
    navigateToShibbolethSignIn(systemConfig.apiEndpoint, window.location.pathname);
  };

  const handleStopSignInAs = async () => {
    try {
      await stopImpersonate();
      log.info('[ProfileMenu] Stopped impersonating');
      showSnackbar('Stopped signing in as another user', 'success');
      onClose(); // Close the profile menu
    } catch (err) {
      log.error('[ProfileMenu] Failed to stop impersonation:', err);
      showSnackbar('Failed to stop sign in as', 'error');
    }
  };

  const handleRetryUserData = async () => {
    try {
      const { fetchUserDataFromServer } = useAuthStore.getState();
      await fetchUserDataFromServer();
      log.info('[ProfileMenu] Retried fetching user data');
    } catch (error) {
      log.error('[ProfileMenu] Failed to retry user data:', error);
    }
  };

  const handleOpenSignOutDialog = () => {
    onClose();
    setSignOutDialogOpen(true);
  };

  const handleConfirmSignOut = async (scope: LogoutScope) => {
    setSignOutSubmitting(true);
    try {
      log.info('[ProfileMenu] Starting sign-out', { scope });
      await logout({ scope });
      onSignOutComplete?.();
    } catch (err) {
      log.error('[ProfileMenu] Sign-out failed:', err);
      showSnackbar('Sign out failed', 'error');
    } finally {
      setSignOutSubmitting(false);
      setSignOutDialogOpen(false);
    }
  };

  const renderAuthenticatedMenuItems = () => [
    <Typography key="eppn" sx={{ px: 2, py: 1, fontSize: '1rem', color: 'text.secondary', lineHeight: '1.5' }}>
      ({currentUser?.eppn})
    </Typography>,
    <Typography key="currentrole" sx={{ px: 2, py: 1, fontSize: '1rem', color: 'text.secondary', lineHeight: '1.5' }}>
      ({currentUser?.roles?.find((role) => role.id === currentUser?.selectedRole)?.name || 'Role not found'})
    </Typography>,
    <Divider key="divider-preferences" sx={{ my: 1 }} />,
    <MenuItem key="preferences" onClick={onClose} component={Link} href={systemConfig.basePath + '/preferences'}>
      Preferences
    </MenuItem>,
    <MenuItem key="autosave" onClick={(e) => e.stopPropagation()} sx={{ py: 0 }}>
      <FormControlLabel
        control={
          <Switch
            checked={autosaveActive}
            disabled={autosaveSuppressed}
            onChange={(e) => {
              e.stopPropagation();
              setAutosaveEnabled(e.target.checked);
            }}
            size="small"
            onClick={(e) => e.stopPropagation()}
          />
        }
        label="Autosave"
        onClick={(e) => e.stopPropagation()}
        sx={{ m: 0, width: '100%' }}
      />
    </MenuItem>,
    <Divider key="divider-roles" />,
    currentUser?.canSwitchRoles ? (
      <MenuItem key="switchRole" onClick={handleOpenRoleSwitcher}>
        Switch Role
      </MenuItem>
    ) : null,

    currentUser?.usingSignInAs ? (
      <MenuItem key="stopSignInAs" onClick={handleOpenStopSignInAsModal}>
        Stop Sign In As
      </MenuItem>
    ) : (
      isAuthorized(PrivilegeEnum.SignInAs, 'read') && (
        <MenuItem key="signInAs" onClick={handleOpenSignInAsModal}>
          Sign In As
        </MenuItem>
      )
    ),

    <Divider key="divider-signout" />,
    <MenuItem key="signout" onClick={handleOpenSignOutDialog}>
      Sign Out
    </MenuItem>,
  ];

  const renderUnauthenticatedMenuItems = () => {
    const items = [
      <MenuItem
        key="signin"
        onClick={() => {
          onClose();
          onLogin();
        }}
      >
        Sign In with Penn State
      </MenuItem>,
    ];
    if (systemConfig?.enableShibboleth) {
      items.push(
        <MenuItem
          key="shibboleth"
          onClick={() => {
            onClose();
            handleShibbolethSignin();
          }}
        >
          Sign In with InCommon
        </MenuItem>
      );
    }
    return items;
  };

  return (
    <>
      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {error || !currentUser?.roles || currentUser.roles.length === 0
          ? [
              <Typography
                key="error-title"
                sx={{
                  px: 2,
                  py: 1,
                  fontSize: '1rem',
                  color: 'error.main',
                  fontWeight: 'bold',
                }}
              >
                Unable to load user data
              </Typography>,
              <Typography
                key="error-message"
                sx={{
                  px: 2,
                  py: 1,
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                }}
              >
                {error || 'No user roles available'}
              </Typography>,
              <Divider key="error-divider" sx={{ my: 1 }} />,
              <MenuItem key="retry" onClick={handleRetryUserData}>
                Retry
              </MenuItem>,
              <MenuItem key="signout-error" onClick={handleOpenSignOutDialog}>
                Sign Out
              </MenuItem>,
            ]
          : [
              <Typography
                key="greeting"
                sx={{
                  px: 2,
                  py: 1,
                  fontSize: '1rem',
                  color: 'text.primary',
                }}
              >
                Hello, {currentUser?.isAnonymous || !currentUser?.fullName ? 'Community User' : currentUser.fullName}
              </Typography>,
              ...(currentUser?.isAnonymous ? renderUnauthenticatedMenuItems() : renderAuthenticatedMenuItems()),
            ]}
      </Menu>
      <RoleSwitcherDialog
        open={isRoleSwitcherOpen}
        onClose={() => {
          handleCloseRoleSwitcher();
          onClose();
        }}
      />
      <SignInAsDialog
        open={isSignInAsModalOpen}
        onClose={() => {
          setSignInAsModalOpen(false);
          onClose();
        }}
      />
      <StopSignInAsDialog
        open={isStopSignInAsModalOpen}
        onClose={handleCloseStopSignInAsModal}
        onConfirm={handleStopSignInAs}
      />
      <SignOutDialog
        open={isSignOutDialogOpen}
        onClose={() => setSignOutDialogOpen(false)}
        authMethod={currentUser?.authMethod}
        onConfirm={handleConfirmSignOut}
        isSubmitting={isSignOutSubmitting}
      />
    </>
  );
}
